import { post } from "@libs:axios";
import {
  GetEntitiesFromFileApiEntitiesFilePostData,
  GetEntitiesFromTextApiEntitiesTextPostData,
  GetEntitiesFromUrlApiEntitiesUrlPostData,
} from "@openapi:types.gen";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { omit } from "lodash";
import { CamelCasedPropertiesDeep } from "type-fest/source/camel-cased-properties-deep";

export interface Entities {
  FILE: {
    RequestBody: CamelCasedPropertiesDeep<
      GetEntitiesFromFileApiEntitiesFilePostData["body"]
    >;
    RequestQuery: CamelCasedPropertiesDeep<
      GetEntitiesFromFileApiEntitiesFilePostData["query"]
    >;
    Response: string;
  };
  TEXT: {
    RequestBody: CamelCasedPropertiesDeep<
      GetEntitiesFromTextApiEntitiesTextPostData["body"]
    >;
    RequestQuery: CamelCasedPropertiesDeep<
      GetEntitiesFromTextApiEntitiesTextPostData["query"]
    >;
    Response: string;
  };
  URL: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetEntitiesFromUrlApiEntitiesUrlPostData["query"]
    >;
    Response: string;
  };
}

export const useMutationEntitiesFile = (
  options?: Omit<
    UseMutationOptions<
      Entities["FILE"]["Response"],
      AxiosError,
      Entities["FILE"]["RequestBody"] & Entities["FILE"]["RequestQuery"]
    >,
    "mutationKey"
  >,
) => {
  return useMutation<
    Entities["FILE"]["Response"],
    AxiosError,
    Entities["FILE"]["RequestBody"] & Entities["FILE"]["RequestQuery"]
  >({
    mutationKey: ["entities", "file"],
    mutationFn: (data) => {
      return post<Entities["FILE"]["Response"]>(
        "/api/entities/file",
        omit(data, ["botId", "nEntities"]),
        {
          params: {
            botId: data.botId,
            nEntities: data.nEntities,
          },
        },
      );
    },
    ...options,
  });
};

export const useMutationEntitiesText = (
  options?: Omit<
    UseMutationOptions<
      Entities["TEXT"]["Response"],
      AxiosError,
      Entities["TEXT"]["RequestBody"] & Entities["TEXT"]["RequestQuery"]
    >,
    "mutationKey"
  >,
) => {
  return useMutation<
    Entities["TEXT"]["Response"],
    AxiosError,
    Entities["TEXT"]["RequestBody"] & Entities["TEXT"]["RequestQuery"]
  >({
    mutationKey: ["entities", "text"],
    mutationFn: (data) => {
      return post<Entities["TEXT"]["Response"]>(
        "/api/entities/text",
        omit(data, ["botId", "nEntities"]),
        {
          params: {
            botId: data.botId,
            nEntities: data.nEntities,
          },
        },
      );
    },
    ...options,
  });
};

export const useMutationEntitiesUrl = (
  options?: Omit<
    UseMutationOptions<
      Entities["URL"]["Response"],
      AxiosError,
      Entities["URL"]["RequestQuery"]
    >,
    "mutationKey"
  >,
) => {
  return useMutation<
    Entities["URL"]["Response"],
    AxiosError,
    Entities["URL"]["RequestQuery"]
  >({
    mutationKey: ["entities", "url"],
    mutationFn: (data) => {
      return post<Entities["URL"]["Response"]>("/api/entities/url", undefined, {
        params: {
          botId: data.botId,
          url: data.url,
          nEntities: data.nEntities,
        },
      });
    },
    ...options,
  });
};

export type EntitiesUnifiedData =
  | { type: "file"; file: File; botId: string; nEntities?: number }
  | { type: "text"; text: string; botId: string; nEntities?: number }
  | { type: "url"; url: string; botId: string; nEntities?: number };

export const useMutationEntities = (
  options?: Omit<
    UseMutationOptions<string, AxiosError, EntitiesUnifiedData>,
    "mutationKey"
  >,
) => {
  return useMutation<string, AxiosError, EntitiesUnifiedData>({
    mutationKey: ["entities"],
    mutationFn: (data) => {
      // Set default nEntities to 5 if not provided
      const nEntities = data.nEntities ?? 5;

      switch (data.type) {
        case "file": {
          const formData = new FormData();
          formData.append("file", data.file);
          return post<string>("/api/entities/file", formData, {
            params: {
              botId: data.botId,
              nEntities,
            },
          });
        }
        case "text":
          return post<string>("/api/entities/text", data.text, {
            params: {
              botId: data.botId,
              nEntities,
            },
          });
        case "url":
          return post<string>("/api/entities/url", undefined, {
            params: {
              botId: data.botId,
              url: data.url,
              nEntities,
            },
          });
      }
    },
    ...options,
  });
};
